import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import Button from '../../../components/atoms/forms/Button';

// Floating action shelf anchored to the bottom of the viewport. Appears as
// soon as a supervisor checks any route-header consolidation checkbox; gives
// them a persistent way to consolidate or clear the selection without
// scrolling back to the routes header. Mounted at the page level (outside
// individual route cards) so it stays put when scrolling through long route
// lists.

const StyledDiv = styledComponent('div', {
  position: 'fixed',
  left: '50%',
  bottom: theme.spacing.sectionGap,
  transform: 'translateX(-50%)',
  background: theme.colors.textDark,
  color: theme.colors.white,
  borderRadius: theme.radii.card,
  boxShadow: theme.shadows.dropdown,
  padding: `${theme.spacing.gapXl} ${theme.spacing.bodyPadding}`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing.fieldGap,
  zIndex: 1000,
  ...theme.typography.body,

  '& .cs-summary': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.gapMd,
  },
  '& .cs-count': {
    fontWeight: theme.fontWeights.semibold,
  },
  '& .cs-divider': {
    color: theme.colors.textHint,
  },
  '& .cs-context': {
    color: theme.colors.textHint,
    fontSize: theme.sizes.smallFontSize,
  },
  '& .cs-actions': {
    display: 'flex',
    gap: theme.spacing.gapMd,
    alignItems: 'center',
  },
});

function ConsolidationShelf({
  selectedRouteCount,
  onClear,
  onConsolidate,
  consolidateDisabled,
}) {
  if (selectedRouteCount < 1) return null;
  const routeWord = selectedRouteCount === 1 ? 'route' : 'routes';
  return (
    <StyledDiv
      role="region"
      aria-label="Consolidation actions"
      data-testid="consolidation-shelf"
    >
      <div className="cs-summary">
        <span>
          <span className="cs-count">{selectedRouteCount}</span> {routeWord}{' '}
          selected
        </span>
        <span className="cs-divider" aria-hidden="true">
          ·
        </span>
        <span className="cs-context">for batch NextMV reroute</span>
      </div>
      <div className="cs-actions">
        <Button
          variant="secondary-inverse"
          onClick={onClear}
          dataTestId="consolidation-shelf-clear"
        >
          Clear
        </Button>
        <Button
          variant="consolidate"
          onClick={onConsolidate}
          disabled={consolidateDisabled}
          title={
            consolidateDisabled
              ? 'Routes are re-optimizing — wait for the current run to finish'
              : undefined
          }
          dataTestId="consolidation-shelf-consolidate"
        >
          🔀 Consolidate Selected
        </Button>
      </div>
    </StyledDiv>
  );
}

ConsolidationShelf.propTypes = {
  selectedRouteCount: PropTypes.number.isRequired,
  onClear: PropTypes.func.isRequired,
  onConsolidate: PropTypes.func.isRequired,
  consolidateDisabled: PropTypes.bool,
};

ConsolidationShelf.defaultProps = {
  consolidateDisabled: false,
};

export default ConsolidationShelf;
